import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Languages, Sparkles } from 'lucide-react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  getAiProviderSettings,
  getAiProviderOptions,
  updateAiProviderSettings,
  updateAiTranslationLanguages,
  getAiTeamUserLimits,
  updateAiTeamUserLimit
} from '../../services/api/implementation/impl';
import toast from 'react-hot-toast';

const PROVIDER_MODEL_HINTS = {
  platform: 'Uses Housingram platform key (gpt-4o-mini)',
  openai: 'e.g. gpt-4o-mini, gpt-4o',
  anthropic: 'e.g. claude-3-5-haiku-latest',
  google: 'e.g. gemini-2.0-flash'
};

/** Guard against a browser-autofilled password being saved as an API key. */
function looksLikeApiKey(provider, rawKey) {
  const key = (rawKey || '').trim();
  if (key.length < 20) return false;
  if (/\s/.test(key)) return false;
  if (provider === 'openai') return /^sk-/.test(key);
  if (provider === 'anthropic') return /^sk-ant-/.test(key);
  return true;
}

export default function AiProviderSettingsModal({ onClose }) {
  const queryClient = useQueryClient();
  const [provider, setProvider] = useState('platform');
  const [useOwnKey, setUseOwnKey] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [editingUserId, setEditingUserId] = useState(null);
  const [userCapDraft, setUserCapDraft] = useState('0');
  const [userEnabledDraft, setUserEnabledDraft] = useState(true);
  const [enabledLanguages, setEnabledLanguages] = useState(['en']);

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['aiProviderSettings'],
    queryFn: getAiProviderSettings
  });

  const { data: providerOptions } = useQuery({
    queryKey: ['aiProviderOptions'],
    queryFn: getAiProviderOptions
  });

  const { data: teamLimits, isLoading: limitsLoading } = useQuery({
    queryKey: ['aiTeamUserLimits'],
    queryFn: getAiTeamUserLimits,
    enabled: !!settingsData?.canManage
  });

  useEffect(() => {
    const p = settingsData?.provider;
    if (!p) return;
    setProvider(p.provider || 'platform');
    setUseOwnKey(!!p.useOwnKey);
    setDefaultModel(p.defaultModel || '');
  }, [settingsData]);

  useEffect(() => {
    const enabled = settingsData?.translationLanguages?.enabled;
    if (Array.isArray(enabled) && enabled.length) {
      setEnabledLanguages(enabled);
    }
  }, [settingsData?.translationLanguages?.enabled]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      await updateAiProviderSettings({
        provider,
        useOwnKey: provider !== 'platform' && useOwnKey,
        defaultModel: defaultModel.trim() || null,
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {})
      });
      await updateAiTranslationLanguages(enabledLanguages);
    },
    onSuccess: () => {
      toast.success('AI settings saved');
      setApiKey('');
      queryClient.invalidateQueries({ queryKey: ['aiProviderSettings'] });
      queryClient.invalidateQueries({ queryKey: ['myAiCredits'] });
      queryClient.invalidateQueries({ queryKey: ['aiTranslationLanguages'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not save AI settings');
    }
  });

  const userLimitMutation = useMutation({
    mutationFn: ({ userId, monthlyTokenCap, llmEnabled }) =>
      updateAiTeamUserLimit(userId, { monthlyTokenCap, llmEnabled }),
    onSuccess: () => {
      toast.success('User AI limit updated');
      setEditingUserId(null);
      queryClient.invalidateQueries({ queryKey: ['aiTeamUserLimits'] });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.message || 'Could not update user limit');
    }
  });

  const canManage = settingsData?.canManage;
  const credits = settingsData?.credits;
  const providerView = settingsData?.provider;
  const options = providerOptions?.providers || [];
  const availableLanguages = settingsData?.translationLanguages?.available || [];

  const toggleLanguage = (code) => {
    if (code === 'en') return;
    setEnabledLanguages((prev) => {
      if (prev.includes(code)) {
        const next = prev.filter((c) => c !== code);
        return next.length ? next : ['en'];
      }
      return [...prev, code];
    });
  };

  const handleSaveSettings = () => {
    const trimmedKey = apiKey.trim();
    if (enabledLanguages.filter((c) => c !== 'en').length === 0) {
      toast.error('Select at least one translation language besides English.');
      return;
    }
    if (provider !== 'platform' && useOwnKey) {
      const alreadyConfigured = providerView?.apiKeyConfigured;
      if (!trimmedKey && !alreadyConfigured) {
        toast.error('Enter your API key to use your own AI provider.');
        return;
      }
      if (trimmedKey && !looksLikeApiKey(provider, trimmedKey)) {
        toast.error(
          'That does not look like a valid API key. Paste the full key from your provider (OpenAI keys start with "sk-").'
        );
        return;
      }
    }
    saveMutation.mutate();
  };

  const startEditUser = (row) => {
    setEditingUserId(row.userId);
    setUserCapDraft(String(row.monthlyTokenCap ?? 0));
    setUserEnabledDraft(row.llmEnabled !== false);
  };

  return (
    <Modal isDirty confirmOnClose name="AI Integration" onClose={onClose} width={56}>
      <div className="space-y-6 p-1">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-teal-50 p-2 text-[#1E4A53]">
            <Sparkles className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">AI Integration</h2>
            <p className="text-sm text-gray-600">
              Use Housingram&apos;s platform key or connect your own OpenAI, Claude, or Gemini key.
              When platform credits run out, your own key keeps AI working without contacting support.
            </p>
          </div>
        </div>

        {isLoading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : (
          <>
            {credits?.provider?.billingMode === 'platform_credits' ? (
              <div
                className={`rounded-xl border px-4 py-3 text-sm ${
                  credits?.depleted
                    ? 'border-amber-300 bg-amber-50 text-amber-950'
                    : 'border-gray-200 bg-gray-50 text-gray-700'
                }`}>
                <p className="font-medium text-gray-900">Platform credits</p>
                <p className="mt-1">
                  Balance: <strong>{credits.creditBalance?.toLocaleString()}</strong> tokens
                  {credits.monthlyTokenCap > 0
                    ? ` · ${credits.tokensUsedThisMonth}/${credits.monthlyTokenCap} used this month`
                    : ''}
                </p>
                {credits?.depleted && canManage ? (
                  <p className="mt-2 text-xs leading-relaxed">
                    Credits are used up. Select OpenAI below, enable &quot;Use my own API key&quot;, and
                    paste your key to continue immediately.
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="rounded-xl border border-teal-200 bg-teal-50 px-4 py-3 text-sm text-teal-900">
                Billing via your own API key — platform credits are not deducted.
              </div>
            )}

            {/* Decoy fields absorb Chrome/Safari password autofill so it never
                lands in the API key or model inputs below. */}
            <div aria-hidden="true" className="hidden">
              <input type="text" name="username" autoComplete="username" tabIndex={-1} />
              <input type="password" name="password" autoComplete="new-password" tabIndex={-1} />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                  Provider
                </label>
                <select
                  value={provider}
                  disabled={!canManage}
                  onChange={(e) => {
                    const next = e.target.value;
                    setProvider(next);
                    if (next === 'platform') setUseOwnKey(false);
                  }}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50">
                  {options.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                  Default model
                </label>
                <input
                  type="text"
                  name="ai-default-model"
                  autoComplete="off"
                  value={defaultModel}
                  disabled={!canManage}
                  onChange={(e) => setDefaultModel(e.target.value)}
                  placeholder={PROVIDER_MODEL_HINTS[provider] || 'Model name'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                />
              </div>
            </div>

            {provider !== 'platform' ? (
              <>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={useOwnKey}
                    disabled={!canManage}
                    onChange={(e) => setUseOwnKey(e.target.checked)}
                  />
                  Use my own API key for this builder
                </label>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">
                    API key
                  </label>
                  <input
                    type="password"
                    name="ai-provider-key"
                    autoComplete="new-password"
                    spellCheck={false}
                    value={apiKey}
                    disabled={!canManage || !useOwnKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder={
                      providerView?.apiKeyConfigured
                        ? `Configured (${providerView.apiKeyMasked}) — enter to replace`
                        : options.find((o) => o.code === provider)?.keyHint || 'Paste API key'
                    }
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-gray-50"
                  />
                </div>
              </>
            ) : (
              <p className="text-xs text-gray-500">
                Platform mode uses Housingram&apos;s OpenAI key from server config. No key needed
                here.
              </p>
            )}

            {canManage ? (
              <div className="rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                <div className="mb-3 flex items-start gap-2">
                  <Languages className="mt-0.5 h-4 w-4 text-[#1E4A53]" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">Translation languages</h3>
                    <p className="mt-1 text-xs leading-relaxed text-gray-600">
                      Choose which languages your team can use when translating AI summaries,
                      WhatsApp drafts, and emails. English is always kept as the source language.
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {availableLanguages.map((lang) => {
                    const checked = enabledLanguages.includes(lang.code);
                    const isEnglish = lang.code === 'en';
                    return (
                      <label
                        key={lang.code}
                        className={`inline-flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                          checked
                            ? 'border-[#2A565E] bg-[#E8F4F6] text-[#1E4A53]'
                            : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                        } ${isEnglish ? 'cursor-default opacity-90' : ''}`}>
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#2A565E]"
                          checked={checked}
                          disabled={isEnglish}
                          onChange={() => toggleLanguage(lang.code)}
                        />
                        <span>
                          {lang.label}
                          <span className="ml-1 text-gray-500">({lang.nativeLabel})</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ) : null}

            {!canManage ? (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
                Only the builder account owner can change API keys and team limits. Ask them to open
                Integrations → AI Integration if you need more AI access.
              </p>
            ) : (
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={handleSaveSettings}
                  disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Saving…' : 'Save settings'}
                </Button>
              </div>
            )}

            {canManage ? (
              <div className="border-t pt-4">
                <h3 className="mb-2 text-sm font-semibold text-gray-900">Team AI limits</h3>
                <p className="mb-3 text-xs text-gray-500">
                  Set monthly token caps per company user (0 = unlimited). Disable AI for specific
                  users if needed.
                </p>
                {limitsLoading ? (
                  <p className="text-sm text-gray-500">Loading team…</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto rounded-xl border border-gray-200">
                    <table className="w-full text-left text-sm">
                      <thead className="sticky top-0 bg-gray-50 text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">User</th>
                          <th className="px-3 py-2">Monthly cap</th>
                          <th className="px-3 py-2">Used</th>
                          <th className="px-3 py-2">AI</th>
                          <th className="px-3 py-2" />
                        </tr>
                      </thead>
                      <tbody>
                        {(teamLimits?.users || []).map((row) => (
                          <tr key={row.userId} className="border-t border-gray-100">
                            <td className="px-3 py-2">
                              <p className="font-medium text-gray-900">{row.userName || '—'}</p>
                              <p className="text-xs text-gray-500">{row.userEmail}</p>
                            </td>
                            <td className="px-3 py-2">
                              {editingUserId === row.userId ? (
                                <input
                                  type="number"
                                  min="0"
                                  value={userCapDraft}
                                  onChange={(e) => setUserCapDraft(e.target.value)}
                                  className="w-24 rounded border px-2 py-1 text-sm"
                                />
                              ) : (
                                row.monthlyTokenCap || '∞'
                              )}
                            </td>
                            <td className="px-3 py-2">{row.tokensUsedThisMonth}</td>
                            <td className="px-3 py-2">
                              {editingUserId === row.userId ? (
                                <input
                                  type="checkbox"
                                  checked={userEnabledDraft}
                                  onChange={(e) => setUserEnabledDraft(e.target.checked)}
                                />
                              ) : row.llmEnabled ? (
                                'On'
                              ) : (
                                'Off'
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {editingUserId === row.userId ? (
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-[#1E4A53]"
                                  onClick={() =>
                                    userLimitMutation.mutate({
                                      userId: row.userId,
                                      monthlyTokenCap: parseInt(userCapDraft, 10) || 0,
                                      llmEnabled: userEnabledDraft
                                    })
                                  }>
                                  Save
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="text-xs font-semibold text-[#1E4A53]"
                                  onClick={() => startEditUser(row)}>
                                  Edit
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ) : null}
          </>
        )}
      </div>
    </Modal>
  );
}
